"use client";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { fetchUserStats, fetchWeeklySummary } from "../../api/api";
import NewAdmissions from "../../mycomp/Dashboard/School/NewAdmissions";
import Courses from "../../mycomp/Dashboard/LMS/PersonalWebUsage";
import RevenueGrowth from "../../mycomp/Dashboard/CRM/RevenueGrowth";
import LeadConversion from "../../mycomp/Dashboard/CRM/LeadConversion";
import TotalOrders from "../../mycomp/Dashboard/CRM/TotalOrders";
import AnnualProfit from "../../mycomp/Dashboard/CRM/AnnualProfit";

import { DateRange } from "react-date-range";
import { addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const PersonalStatistics = () => {
  const location = useLocation();
  const user_id = location.state?.user_id;
  const name = location.state?.name || "Unknown User";
  const email = location.state?.email || "No Email";
  const workspace = location.state?.workspace_name || "No Workspace";

  const [totalWorked, setTotalWorked] = useState("00h 00m");
  const [totalActive, setTotalActive] = useState("00h 00m");
  const [productivity, setProductivity] = useState(0);
  const [idlePercent, setIdlePercent] = useState(0);
  const [clockIn, setClockIn] = useState("-");
  const [clockOut, setClockOut] = useState("-");
  const [summary, setSummary] = useState([]);

  // helpers
  const formatDuration = (seconds) => {
    const sec = Number(seconds) || 0;
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const formatClock = (time) => {
    if (!time) return "-";
    return new Date(time).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString();
  };

  const [showPicker, setShowPicker] = useState(false);

  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);



  useEffect(() => {
    if (!user_id) return;

    const loadStats = async () => {
      try {
        const data = await fetchUserStats(user_id);

        const worked = Number(data.total_worked_seconds) || 0;
        const active = Number(data.total_active_seconds) || 0;

        setTotalWorked(formatDuration(worked));
        setTotalActive(formatDuration(active));

        setProductivity(Math.min(data.productive_percent || 0, 100));
        setIdlePercent(Math.min(data.idle_percent || 0, 100));

        setClockIn(formatClock(data.today_clock_in));
        setClockOut(formatClock(data.today_clock_out));
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      }
    };

    const loadSummary = async () => {
      try {
        const data = await fetchWeeklySummary(user_id);
        console.log("Weekly summary API result:", data);

        if (Array.isArray(data)) {
          // Cap productivity at 100%
          const capped = data.map(row => ({
            ...row,
            productivity: row.productivity > 100 ? 100 : row.productivity
          }));
          setSummary(capped);
        } else if (data.summary && Array.isArray(data.summary)) {
          const capped = data.summary.map(row => ({
            ...row,
            productivity: row.productivity > 100 ? 100 : row.productivity
          }));
          setSummary(capped);
        } else {
          setSummary([]);
        }
      } catch (err) {
        console.error("Failed to fetch weekly summary:", err);
        setSummary([]);
      }
    };

    loadStats();
    loadSummary();
  }, [user_id]);

  return (
    <>
      {/* USER HEADER */}
      <Row className="mb-3">
        <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-6 col-sm-12">
          <Card className="bg-primary border-0 rounded-3 welcome-box py-5 px-4">
            <div className="d-flex align-items-center">
              <div
                className="d-flex justify-content-center align-items-center me-3"
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  backgroundColor: "#ffffff30",
                  color: "#fff",
                  fontSize: "28px",
                  fontWeight: "600",
                }}
              >
                {name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="mb-1 text-white">{name}</h2>
                <p className="mb-0 text-light">{email}</p>
                {/* <p className="mb-0 text-light" style={{ fontSize: "14px", opacity: 0.8 }}>
                  {workspace}
                </p> */}
              </div>
            </div>
          </Card>
        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3">
          <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
            <p>Total Worked Hours</p>
            <h2>{totalWorked}</h2>
            <p>Clocked-in hours (excludes breaks & screen lock)</p>
          </div>
        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3">
          <div className="card bg-white bg-opacity-10 p-2 rounded-3">
            <p>Active Time</p>
            <h2>{totalActive}</h2>
            <p>Time spent actively engaging with the system</p>
          </div>
        </div>
      </Row>

      {/* METRICS */}
      <Row className="mb-3">
        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3 mt-3">
          <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
            <p>Productive Time Spent %</p>
            <h2>{productivity}%</h2>
            <p>Percentage of Productivity</p>
          </div>
        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3 mt-3">
          <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
            <p>Idle Time Spent %</p>
            <h2>{idlePercent}%</h2>
            <p>No system activity (inactive period)</p>
          </div>
        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3 mt-3">
          <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
            <p>Clock-in Time</p>
            <h2>{clockIn}</h2>
            <p>Start time recorded for today</p>
          </div>
        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-3 col-sm-3 mt-3">
          <div className="card bg-white bg-info bg-opacity-10 p-2 rounded-3">
            <p>Clock-out Time</p>
            <h2>{clockOut}</h2>
            <p>End time recorded for today</p>
          </div>
        </div>
      </Row>

      {/* <Row>
           <Col xs={12} sm={6} lg={6} xl={6} xxl={3}>
          <RevenueGrowth />
        </Col>

        <Col xs={12} sm={6} lg={6} xl={6} xxl={3}>
          <LeadConversion />
        </Col>

        <Col xs={12} sm={6} lg={6} xl={6} xxl={3}>
          <TotalOrders />
        </Col>

        <Col xs={12} sm={6} lg={6} xl={6} xxl={3}>
          <AnnualProfit />
        </Col>
      </Row> */}

      {/* PLACEHOLDERS */}
      <Row className="mb-3">
        <Col sm={12} lg={8} xl={8} xxl={8}>
          <Courses user_id={user_id} />
        </Col>
        <Col sm={12} lg={4} xl={4} xxl={4}>
          <NewAdmissions user_id={user_id} />
        </Col>
      </Row>

      {/* WEEKLY SUMMARY TABLE */}
      <Row className="mb-3">
        <div className="col-12 mt-3 mb-3">
          <div className="card bg-white bg-info bg-opacity-10 p-4 rounded-3">
            <h3 className="mb-4">Clock In & Out Summary (Last 7 Days)</h3>
            <div className="table-responsive">
              <table className="table align-middle table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Total Worked</th>
                    <th>Active Time</th>
                    <th>Idle Time</th>
                    <th>Productivity</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    summary.map((row, index) => (
                      <tr key={index}>
                        <td>{formatDate(row.date)}</td>
                        <td>{formatClock(row.clock_in)}</td>
                        <td>{formatClock(row.clock_out)}</td>
                        <td>{formatDuration(row.worked_seconds)}</td>
                        <td>{formatDuration(row.active_seconds)}</td>
                        <td>{formatDuration(row.idle_seconds)}</td>
                        <td>{row.productivity}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Row>
    </>
  );
};

export default PersonalStatistics;